import { useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { Link } from 'react-router-dom'

import { analyser } from '../../analysis/engine'
import { episodesDerives } from '../../analysis/evenements'
import {
  construireSuggestions,
  couverture,
  statutsIngredients,
  type Suggestion,
} from '../../analysis/suggestions'
import { NIVEAU_LABELS, VERDICT_LABELS, type NiveauAnalyse, type Verdict } from '../../analysis/types'
import { db } from '../../db/db'
import { SYMPTOM_LABELS } from '../../domain/types'
import { Carte, Entete, IconeChevron, Jauge, Puce, Vide } from '../composants'

const TON_VERDICT: Record<Verdict, 'ok' | 'alerte' | 'danger' | 'neutre' | 'info'> = {
  'piste-serieuse': 'danger',
  'a-surveiller': 'alerte',
  'probablement-non-lie': 'ok',
  'donnees-insuffisantes': 'neutre',
}

export function formaterRR(rr: number): string {
  if (!Number.isFinite(rr)) return '∞'
  return `×${rr.toFixed(1)}`
}

export function PageAnalyses() {
  const [niveau, setNiveau] = useState<NiveauAnalyse | 'tous'>('tous')

  const donnees = useLiveQuery(
    async () => ({
      repas: await db.meals.toArray(),
      sensations: await db.sensations.toArray(),
      selles: await db.stools.toArray(),
      journaux: await db.dailyLogs.toArray(),
      ingredients: await db.ingredients.toArray(),
      recettes: await db.recipes.toArray(),
    }),
    [],
  )

  const resultat = useMemo(() => {
    if (!donnees) return undefined
    // Sensations et selles sont d'abord traduites en poussees : le moteur ne
    // connait que des episodes (cf. `analysis/evenements.ts`).
    const { sensations, selles, ...reste } = donnees
    return analyser({ ...reste, episodes: episodesDerives(sensations, selles) })
  }, [donnees])

  const suggestions = useMemo(
    () =>
      donnees && resultat
        ? construireSuggestions({
            repas: donnees.repas,
            ingredients: donnees.ingredients,
            resultat,
          })
        : [],
    [donnees, resultat],
  )

  const couv = useMemo(
    () =>
      donnees && resultat
        ? couverture(statutsIngredients(donnees.repas, donnees.ingredients, resultat))
        : undefined,
    [donnees, resultat],
  )

  if (!resultat) {
    return (
      <>
        <Entete titre="Analyses" />
        <main className="contenu">
          <p className="doux">Calcul en cours...</p>
        </main>
      </>
    )
  }

  const { qualite } = resultat
  const affichees = resultat.associations.filter(
    (a) =>
      a.verdict !== 'donnees-insuffisantes' &&
      (niveau === 'tous' || a.niveau === niveau),
  )
  const serieuses = affichees.filter((a) => a.verdict === 'piste-serieuse')
  const aSurveiller = affichees.filter((a) => a.verdict === 'a-surveiller')
  const nonLiees = affichees.filter((a) => a.verdict === 'probablement-non-lie')

  return (
    <>
      <Entete titre="Analyses" sousTitre={`${qualite.nbRepas} repas · ${qualite.nbEpisodes} poussées`} />

      <main className="contenu">
        <div className="segments">
          <span className="segment" aria-current="page">
            Corrélations
          </span>
          <Link to="/statistiques" className="segment">
            Statistiques
          </Link>
        </div>

        <div className="encart encart--info">
          Ces résultats sont <strong>exploratoires</strong>. Ils servent à orienter un test de
          réintroduction, pas à poser un diagnostic. Une association statistique n'est pas une
          preuve de causalité.
        </div>

        {/* --- Fiabilite --- */}
        <Carte titre="Fiabilité des analyses">
          <div className="ligne ligne--espacee" style={{ marginBottom: 8 }}>
            <span className="doux">Volume et régularité des données</span>
            <span className="gras mono">{qualite.score}/100</span>
          </div>
          <Jauge valeur={qualite.score} />
          <div className="puces" style={{ marginTop: 12 }}>
            <Puce>{qualite.nbJoursAvecRepas} j avec repas</Puce>
            <Puce>{qualite.nbJoursAvecJournal} j avec journal</Puce>
            <Puce>{qualite.nbJoursSuivis} j de suivi</Puce>
            {qualite.repasTropRecents > 0 && (
              // La fenetre la plus longue va jusqu'a 48 h : ces repas ne sont
              // pas perdus, seulement pas encore jugeables.
              <Puce ton="info">{qualite.repasTropRecents} repas en attente de verdict</Puce>
            )}
          </div>
          {qualite.avertissements.length > 0 && (
            <ul className="pile pile--serree" style={{ marginTop: 12, paddingLeft: 0, listStyle: 'none' }}>
              {qualite.avertissements.map((a, i) => (
                <li key={i} className="tres-doux">
                  — {a}
                </li>
              ))}
            </ul>
          )}
        </Carte>

        {/* --- Ce qu'il faudrait pour en savoir plus --- */}
        {suggestions.length > 0 && (
          <Carte titre="À explorer">
            <p className="doux" style={{ marginBottom: 12 }}>
              Classé par ce qui apporterait le plus d'information. Rien ici ne vous demande de
              manger quoi que ce soit de nouveau : il s'agit de ce qui est déjà à votre menu.
            </p>
            <ul className="pile">
              {suggestions.map((s) => (
                <ListeSuggestion key={s.id} s={s} />
              ))}
            </ul>
          </Carte>
        )}

        {/* --- Couverture du référentiel --- */}
        {couv && (
          <Carte titre="Couverture des aliments">
            <p className="doux" style={{ marginBottom: 12 }}>
              Sur {couv.total} aliments du référentiel, voici ce que vos données permettent
              aujourd'hui d'en dire. Un aliment jamais consommé n'est <strong>pas</strong> un
              aliment innocenté : l'analyse n'a simplement rien à en dire.
            </p>
            <div className="puces">
              {couv.piste > 0 && <Puce ton="danger">{couv.piste} piste(s) sérieuse(s)</Puce>}
              {couv.surveiller > 0 && <Puce ton="alerte">{couv.surveiller} à surveiller</Puce>}
              <Puce ton="ok">{couv.analyse} analysé(s)</Puce>
              <Puce ton="info">{couv.insuffisant} avec trop peu de données</Puce>
              <Puce>{couv.jamais} jamais consommé(s)</Puce>
            </div>
          </Carte>
        )}

        {/* --- Filtre de niveau --- */}
        <div className="segments">
          <button
            type="button"
            className="segment"
            aria-pressed={niveau === 'tous'}
            onClick={() => setNiveau('tous')}
          >
            Tous
          </button>
          {(['ingredient', 'famille', 'charge', 'categorie'] as NiveauAnalyse[]).map((n) => (
            <button
              key={n}
              type="button"
              className="segment"
              aria-pressed={niveau === n}
              onClick={() => setNiveau(n)}
            >
              {n === 'ingredient'
                ? 'Aliment'
                : n === 'famille'
                  ? 'FODMAP'
                  : n === 'charge'
                    ? 'Charge'
                    : 'Catégorie'}
            </button>
          ))}
        </div>

        {/* --- Blocs indissociables --- */}
        {resultat.blocsIndissociables.length > 0 && (
          <Carte titre="Aliments indissociables">
            <p className="doux" style={{ marginBottom: 10 }}>
              Ces aliments apparaissent presque toujours ensemble dans vos repas. Leurs
              statistiques sont donc identiques et <strong>aucune donnée actuelle ne permet de
              les départager</strong>. Pour les séparer, il faudrait en consommer un sans l'autre.
            </p>
            <div className="pile pile--serree">
              {resultat.blocsIndissociables.map((b, i) => (
                <div key={i} className="puces">
                  {b.libelles.map((l) => (
                    <Puce key={l} ton="info">
                      {l}
                    </Puce>
                  ))}
                </div>
              ))}
            </div>
          </Carte>
        )}

        {affichees.length === 0 ? (
          <Vide>
            Aucune association exploitable pour le moment.
            <br />
            Il faut généralement plusieurs semaines de journal avant que les premiers résultats
            deviennent interprétables.
          </Vide>
        ) : (
          <>
            <ListeAssociations
              titre="Pistes sérieuses"
              vide="Aucune piste ne franchit le seuil pour l'instant. C'est fréquent et ce n'est pas un mauvais signe."
              associations={serieuses}
            />
            <ListeAssociations
              titre="A surveiller"
              vide="Rien a surveiller pour l'instant."
              associations={aSurveiller}
            />
            <ListeAssociations
              titre="Probablement non liées"
              vide=""
              associations={nonLiees.slice(0, 15)}
              repliable
            />
          </>
        )}
      </main>
    </>
  )
}

const TON_SUGGESTION: Record<Suggestion['type'], 'danger' | 'alerte' | 'info' | 'neutre'> = {
  'a-isoler': 'danger',
  'a-confirmer': 'alerte',
  'trop-peu': 'info',
  'jamais-teste': 'neutre',
}

const LIBELLE_SUGGESTION: Record<Suggestion['type'], string> = {
  'a-isoler': 'À isoler',
  'a-confirmer': 'À confirmer',
  'trop-peu': 'Trop peu de données',
  'jamais-teste': 'Jamais testé',
}

function ListeSuggestion({ s }: { s: Suggestion }) {
  return (
    <li className="carte carte--plate" style={{ padding: 12 }}>
      <div className="ligne ligne--espacee ligne--haut" style={{ marginBottom: 6 }}>
        <span className="item__principal grandir">{s.titre}</span>
        <Puce ton={TON_SUGGESTION[s.type]}>{LIBELLE_SUGGESTION[s.type]}</Puce>
      </div>
      <p className="doux">{s.explication}</p>
      <p className="doux gras" style={{ marginTop: 6 }}>
        → {s.action}
      </p>
    </li>
  )
}

function ListeAssociations({
  titre,
  vide,
  associations,
  repliable,
}: {
  titre: string
  vide: string
  associations: ReturnType<typeof analyser>['associations']
  repliable?: boolean
}) {
  const [ouvert, setOuvert] = useState(!repliable)

  return (
    <Carte
      titre={`${titre} (${associations.length})`}
      action={
        repliable ? (
          <button
            type="button"
            className="btn btn--fantome btn--petit"
            onClick={() => setOuvert((o) => !o)}
          >
            {ouvert ? 'Masquer' : 'Afficher'}
          </button>
        ) : undefined
      }
    >
      {!ouvert ? null : associations.length === 0 ? (
        vide ? (
          <p className="doux">{vide}</p>
        ) : null
      ) : (
        <ul className="liste">
          {associations.map((a) => (
            <li key={`${a.cle}-${a.symptome}`}>
              <Link
                to={`/analyses/${encodeURIComponent(a.cle)}/${a.symptome}`}
                className="item"
              >
                <div className="grandir">
                  <div className="item__principal">{a.libelle}</div>
                  <div className="item__secondaire">
                    {SYMPTOM_LABELS[a.symptome]} · {NIVEAU_LABELS[a.niveau]}
                  </div>
                  <div className="tres-doux" style={{ marginTop: 3 }}>
                    {a.contingence.a}/{a.repasExposes} repas exposés suivis d'un déclenchement ·{' '}
                    {a.episodesDistincts} episode{a.episodesDistincts > 1 ? 's' : ''} distinct
                    {a.episodesDistincts > 1 ? 's' : ''}
                  </div>
                  {/* La fenetre n'est pas un detail technique : c'est elle qui
                      nomme le mecanisme, donc ce qu'il faut changer. */}
                  <div className="puces" style={{ marginTop: 5 }}>
                    <Puce ton="info">{a.fenetre.libelle} après le repas</Puce>
                    <Puce>{a.fenetre.mecanisme}</Puce>
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div className="gras mono">{formaterRR(a.risqueRelatif)}</div>
                  <Puce ton={TON_VERDICT[a.verdict]}>{VERDICT_LABELS[a.verdict]}</Puce>
                </div>
                <span className="chevron">
                  <IconeChevron />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Carte>
  )
}
